import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardHeader
} from '@heroui/react';
import { SparklesIcon, TrophyIcon, BuildingLibraryIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { usePlanes } from '@/services/plataforma/usePlanes';

interface SubscriptionPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPlanSelect?: (planName: string) => void;
}

const SubscriptionPlanModal: React.FC<SubscriptionPlanModalProps> = ({ isOpen, onClose, onPlanSelect }) => {
  // Mock data for current user's subscription plan
  const currentPlan = "Empresarial"; // This would come from the user's subscription data

  // Fetch plans from API
  const { data: plansData, loading, error } = usePlanes();

  // Map API data to component format
  const plans = plansData?.map((plan, index) => ({
    name: plan.nombre,
    icon: index === 1 ? TrophyIcon : index === 0 ? SparklesIcon : BuildingLibraryIcon, // Default icons based on position
    price: plan.precioMensual === 0 ? 'Gratis' : `$${plan.precioMensual.toLocaleString('es-CO')}`,
    period: plan.precioMensual > 0 ? '/mes' : '',
    description: plan.descripcion,
    features: plan.caracteristicas.map(feat => {
      // Format each feature based on the API structure
      if (feat.precioUnitario === 0) {
        return `${feat.cantidad} ${feat.caracteristica}`;
      } else {
        return `${feat.cantidad} ${feat.caracteristica} - $${feat.precioUnitario.toLocaleString('es-CO')}`;
      }
    }),
    popular: plan.nombre.toLowerCase() === 'pyme' // Assuming PYME is the popular plan
  })) || [];

  // State to manage expanded/collapsed state for each card's features - default to collapsed
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});

  const handlePlanSelect = (planName: string) => {
    // In a real implementation, this would handle the plan selection process
    console.log(`Plan selected: ${planName}`);
    onPlanSelect?.(planName);
    onClose();
  };

  const toggleCard = (index: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onClose}
      placement="center"
      size="4xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        {(_onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-2xl font-bold">Selecciona tel plan adecuado a tu empresa</h3>
              <p className="text-gray-600 dark:text-gray-300">Elige el plan que mejor se adapte a las necesidades de tu empresa</p>
            </ModalHeader>
            <ModalBody className="py-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {!loading && !error ? (
                  plans.map((plan, index) => {
                    const isCurrentPlan = plan.name === currentPlan;
                    const isExpanded = expandedCards[index] ?? false; // Default to collapsed
                    
                    return (
                      <Card
                        key={plan.name}
                        className={`relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border transition-all duration-300 hover:scale-105 ${
                          plan.popular
                            ? 'border-orange-500 dark:border-orange-400 shadow-2xl shadow-orange-500/20 dark:shadow-orange-900/30'
                            : 'border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-400'
                        } ${isCurrentPlan ? 'ring-2 ring-blue-500' : ''}`}
                      >
                        <CardHeader className='flex flex-col items-center'>
                          <div className="flex flex-col items-center">
                            {plan.popular && (
                              <div className="">
                                <span className="bg-orange-500 text-white px-10 py-1.5 rounded-full text-sm font-bold shadow-lg dark:bg-orange-600">
                                  Más popular
                                </span>
                              </div>
                            )}

                            {isCurrentPlan && (
                              <div className="flex">
                                <span className="bg-blue-500 text-white px-10 py-1.5 rounded-full text-sm font-bold shadow-lg dark:bg-blue-600">
                                  Plan Actual
                                </span>
                              </div>
                            )}
                          </div>
                        </CardHeader>

                        <CardBody className="p-8 pt-6">
                          <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h4>
                          <p className="text-gray-600 text-2sm dark:text-gray-300 mb-6">{plan.description}</p>

                          <div className="mb-8">
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                              <span className="text-gray-600 dark:text-gray-300 text-lg">{plan.period}</span>
                            </div>
                          </div>

                          <div className="mb-8">
                            <button 
                              onClick={() => toggleCard(index)}
                              className="flex items-center justify-between w-full text-left focus:outline-none"
                            >
                              <span className="font-medium text-gray-900 dark:text-white">
                                Características ({plan.features.length})
                              </span>
                              <ChevronDownIcon 
                                className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                                  isExpanded ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                            
                            {isExpanded && (
                              <div className="space-y-4 mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                                {plan.features.map((feature, idx) => (
                                  <div key={idx} className="flex items-start gap-3">
                                    <CheckIcon className="w-5 h-5 text-orange-500 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <Button
                            color={isCurrentPlan ? "default" : plan.popular ? "primary" : "default"}
                            variant={isCurrentPlan ? "bordered" : "solid"}
                            className="w-full py-3.5 px-4 rounded-xl font-semibold transition-all"
                            onPress={() => handlePlanSelect(plan.name)}
                            disabled={isCurrentPlan}
                          >
                            {isCurrentPlan ? "Plan Actual" : "Seleccionar Plan"}
                          </Button>
                        </CardBody>
                      </Card>
                    );
                  })
                ) : (
                  <div className="w-full text-center">
                    {loading ? (
                      <p className="text-gray-600 dark:text-gray-300">Cargando planes...</p>
                    ) : (
                      <p className="text-red-600 dark:text-red-400">Error al cargar los planes: {error}</p>
                    )}
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="flat" onClick={onClose}>
                Cerrar
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default SubscriptionPlanModal;